import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui-helpers";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, Link2, Unlink, FilePlus2, CheckCircle2, AlertTriangle } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : "never");
const REASONS = { invalid_state: "the sign-in link expired — try again", token: "Google refused the sign-in code", scope: "spreadsheet access was not allowed — tick the Sheets box when asked", no_refresh_token: "Google did not grant lasting access — remove the app from your Google account permissions and connect again" };

function useSheetStatus() {
  const [st, setSt] = useState(null);
  const load = () => api.get("/google-sheets/status").then((r) => setSt(r.data)).catch(() => setSt(false));
  useEffect(() => { load(); }, []);
  return [st, setSt, load];
}

function LastSync({ st }) {
  if (!st?.spreadsheet_id) return null;
  return st.last_ok === false
    ? <span data-testid="sheet-sync-error" className="inline-flex items-center gap-1 text-xs text-rose-700"><AlertTriangle className="h-3.5 w-3.5" /> Sync failed {fmt(st.last_sync_at)}: {st.last_error}</span>
    : <span data-testid="sheet-sync-last" className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Last synced {fmt(st.last_sync_at)}</span>;
}

async function syncNow(setSt, setBusy, onSynced) {
  setBusy(true);
  try {
    const { data } = await api.post("/short-term/sync");
    setSt(data.status);
    if (data.ok) toast.success(`Synced — ${data.rows} rows (${data.pulled} updated, ${data.created} added from the sheet)`); else toast.error(data.error);
    onSynced?.();
  } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
}

export function SheetSyncBar({ onSynced }) {
  const [st, setSt] = useSheetStatus();
  const [busy, setBusy] = useState(false);
  if (!st) return null;
  return (
    <div data-testid="sheet-sync-bar" className="mb-4 flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm">
      {st.spreadsheet_id ? (<>
        <span className="font-semibold text-slate-800" data-testid="sheet-sync-connected">Google Sheet: {st.spreadsheet_title}</span>
        <LastSync st={st} />
        <a href={st.spreadsheet_url} target="_blank" rel="noreferrer" data-testid="sheet-sync-open" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">Open sheet <ExternalLink className="h-3 w-3" /></a>
        <button data-testid="sheet-sync-now" disabled={busy} onClick={() => syncNow(setSt, setBusy, onSynced)} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 disabled:opacity-60"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Sync now</button>
      </>) : <span data-testid="sheet-sync-not-connected" className="text-slate-500">Google Sheet not connected — an admin can connect one in Settings → Google Sheets sync.</span>}
    </div>
  );
}

function PickSheet({ onPicked }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const pick = async (body) => {
    setBusy(true);
    try { const { data } = await api.post("/google-sheets/spreadsheet", body); onPicked(data); toast[data.sync?.ok ? "success" : "warning"](data.sync?.ok ? "Sheet connected and synced" : `Sheet saved, sync failed: ${data.sync?.error}`); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      <input data-testid="gsheets-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste a Google Sheets link…" className="flex-1 min-w-[240px] px-3 py-2 rounded-lg border border-slate-200 text-sm" />
      <button data-testid="gsheets-use-sheet" disabled={busy || !url.trim()} onClick={() => pick({ url })} className="bg-primary text-white px-4 py-2 rounded-xl text-sm font-semibold disabled:opacity-60">Use this sheet</button>
      <button data-testid="gsheets-create-sheet" disabled={busy} onClick={() => pick({ create: true })} className="inline-flex items-center gap-1.5 border border-slate-200 px-4 py-2 rounded-xl text-sm font-semibold text-slate-700 disabled:opacity-60"><FilePlus2 className="h-4 w-4" /> Create “Short-Term Courses”</button>
    </div>
  );
}

export function GoogleSheetsCard() {
  const [st, setSt, load] = useSheetStatus();
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const g = params.get("google");
    if (!g) return;
    if (g === "connected") toast.success("Google account connected — now choose the spreadsheet");
    else toast.error(`Google connection failed: ${REASONS[params.get("reason")] || params.get("reason") || "unknown error"}`);
    params.delete("google"); params.delete("reason"); setParams(params, { replace: true });
  }, []); // eslint-disable-line
  const connect = async () => {
    try { const { data } = await api.get("/google-sheets/connect"); window.location.href = data.url; } catch (e) { toast.error(apiErr(e)); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google? Short-Term Courses will stop syncing (nothing is deleted).")) return;
    try { await api.post("/google-sheets/disconnect"); toast.success("Google disconnected"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  if (st === null) return null;
  const redirect = `${process.env.REACT_APP_BACKEND_URL}/api/oauth/sheets/callback`;
  return (
    <Card className="p-5 mb-5" data-testid="gsheets-card">
      <h3 className="font-head font-bold text-slate-900">Google Sheets sync — Short-Term Courses</h3>
      <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">Two-way: edits in the CRM appear in the sheet within seconds, and edits in the sheet come back every 5 minutes (or with Sync now). If both sides change the same cell, the newest change wins. Keep the “CRM ID” column untouched.</p>
      {!st?.configured ? (
        <div data-testid="gsheets-not-configured" className="mt-3 text-sm rounded-xl bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 space-y-1">
          <div className="font-semibold">Not set up yet — Google Client ID and Secret are missing.</div>
          <div className="text-xs">Create an OAuth “Web application” client in Google Cloud Console (enable the Google Sheets API), add this authorized redirect URL, then ask your developer to add the Client ID and Secret securely:</div>
          <code data-testid="gsheets-redirect-url" className="block text-xs bg-white border border-amber-200 rounded px-2 py-1 break-all">{redirect}</code>
        </div>
      ) : !st.connected ? (
        <button data-testid="gsheets-connect" onClick={connect} className="mt-3 inline-flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-xl text-sm font-semibold"><Link2 className="h-4 w-4" /> Connect Google</button>
      ) : (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span data-testid="gsheets-connected-as" className="font-semibold text-slate-800">Connected as {st.google_email || "Google account"}</span>
            {st.spreadsheet_id && <a href={st.spreadsheet_url} target="_blank" rel="noreferrer" data-testid="gsheets-open" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">{st.spreadsheet_title} <ExternalLink className="h-3 w-3" /></a>}
            <LastSync st={st} />
            <div className="ml-auto flex gap-2">
              {st.spreadsheet_id && <button data-testid="gsheets-sync-now" disabled={busy} onClick={() => syncNow(setSt, setBusy)} className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 disabled:opacity-60"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Sync now</button>}
              <button data-testid="gsheets-disconnect" onClick={disconnect} className="inline-flex items-center gap-1.5 border border-rose-200 px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-700"><Unlink className="h-3.5 w-3.5" /> Disconnect</button>
            </div>
          </div>
          <PickSheet onPicked={setSt} />
        </div>
      )}
    </Card>
  );
}
